import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  User, MapPin, Phone, Mail, FileText, Pencil, Search,
  CalendarDays, Clock, Users, Star, ChevronRight, Loader2, Send,
  Upload, CheckCircle, X as XIcon, Eye,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GuestProfilePage = () => {
  const { user, token, updateUser } = useContext(AuthContext);
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [recentlyBrowsed, setRecentlyBrowsed] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contactModal, setContactModal] = useState(null);
  const [contactMessage, setContactMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploading, setUploading] = useState({});
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    address: '',
    bio: '',
    proof_of_address: '',
    id_document: '',
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [token]);

  useEffect(() => {
    if (user) {
      setEditForm({
        name: user.name || '',
        phone: user.phone || '',
        address: user.address || '',
        bio: user.bio || '',
        proof_of_address: user.proof_of_address || '',
        id_document: user.id_document || '',
      });
      // Upload pending docs from registration
      const pending = localStorage.getItem('pendingDocs');
      if (pending) {
        try {
          const docs = JSON.parse(pending);
          if (docs.proof_of_address || docs.id_document) {
            axios.put(`${API_URL}/api/auth/profile`, docs, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(res => {
              if (updateUser) updateUser(res.data);
            });
          }
        } catch (err) {
          console.error("Failed to upload pending docs:", err);
        }
        localStorage.removeItem('pendingDocs');
      }
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [browseRes, bookingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/browse-history`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/guest/bookings`, { headers }).catch(() => ({ data: [] })),
      ]);
      setRecentlyBrowsed(browseRes.data || []);
      // Filter upcoming and confirmed bookings only
      const upcoming = (bookingsRes.data || []).filter(b =>
        ['confirmed', 'pending'].includes(b.status)
      );
      setUpcomingBookings(upcoming);
    } catch {
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/auth/profile`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (updateUser) updateUser(res.data);
      setEditing(false);
      toast.success('Profile updated');
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (field, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB');
      return;
    }
    setUploading(prev => ({ ...prev, [field]: true }));
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditForm(prev => ({ ...prev, [field]: res.data.url }));
      toast.success('Document uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleContactHost = async () => {
    if (!contactMessage.trim() || contactMessage.length < 5) {
      toast.error('Please write a message (at least 5 characters)');
      return;
    }
    setSendingMessage(true);
    try {
      await axios.post(`${API_URL}/api/bookings/${contactModal.id}/contact-host`, {
        message: contactMessage,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Message sent to host!');
      setContactModal(null);
      setContactMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#D65A31]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F4EDE4]" data-testid="guest-profile-page">
      <Navbar />
      <div className="h-20" />

      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-8">
        {/* Profile Header */}
        <div className="bg-white rounded-2xl shadow-sm border p-6 mb-6" data-testid="profile-card">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#FDF8F4] flex items-center justify-center text-[#D65A31] text-2xl font-bold">
                {user?.name?.charAt(0)?.toUpperCase() || 'G'}
              </div>
              <div>
                <h1 className="text-2xl font-semibold" data-testid="profile-name">{user?.name}</h1>
                <p className="text-gray-500 text-sm">{user?.email}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setEditing(!editing)} data-testid="edit-profile-btn">
              <Pencil className="w-4 h-4 mr-1" /> {editing ? 'Cancel' : 'Edit'}
            </Button>
          </div>

          {editing ? (
            <div className="space-y-4" data-testid="profile-edit-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1" data-testid="edit-name" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="mt-1" data-testid="edit-phone" />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="mt-1" data-testid="edit-address" />
              </div>
              <div>
                <Label>Bio</Label>
                <Textarea value={editForm.bio} onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })} className="mt-1" rows={3} maxLength={300} data-testid="edit-bio" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Proof of Address</Label>
                  {editForm.proof_of_address ? (
                    <div className="flex items-center gap-2 mt-1">
                      <a href={`${API_URL}${editForm.proof_of_address}`} target="_blank" rel="noreferrer" className="text-sm text-[#D65A31] underline flex items-center gap-1"><Eye className="w-4 h-4" />View</a>
                      <button onClick={() => setEditForm({ ...editForm, proof_of_address: '' })} className="text-gray-400 hover:text-red-500"><XIcon className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-3 mt-1 cursor-pointer hover:border-[#D65A31] transition-colors">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload('proof_of_address', e.target.files[0])} />
                      {uploading.proof_of_address ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-gray-400" />}
                      <span className="text-sm text-gray-500">Upload</span>
                    </label>
                  )}
                </div>
                <div>
                  <Label>ID Document</Label>
                  {editForm.id_document ? (
                    <div className="flex items-center gap-2 mt-1">
                      <a href={`${API_URL}${editForm.id_document}`} target="_blank" rel="noreferrer" className="text-sm text-[#D65A31] underline flex items-center gap-1"><Eye className="w-4 h-4" />View</a>
                      <button onClick={() => setEditForm({ ...editForm, id_document: '' })} className="text-gray-400 hover:text-red-500"><XIcon className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-3 mt-1 cursor-pointer hover:border-[#D65A31] transition-colors">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload('id_document', e.target.files[0])} />
                      {uploading.id_document ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-gray-400" />}
                      <span className="text-sm text-gray-500">Upload</span>
                    </label>
                  )}
                </div>
              </div>
              <Button onClick={handleSaveProfile} disabled={saving} className="bg-[#D65A31] hover:bg-[#B84A28] text-white" data-testid="save-profile-btn">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</> : 'Save changes'}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-testid="profile-info">
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="w-4 h-4 text-gray-400" />
                <span data-testid="profile-phone">{user?.phone || 'No phone'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Mail className="w-4 h-4 text-gray-400" />
                <span>{user?.email}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span data-testid="profile-address">{user?.address || 'No address'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <FileText className="w-4 h-4 text-gray-400" />
                <span>Docs: {user?.proof_of_address ? 'Proof ' : ''}{user?.id_document ? 'ID' : ''}{!user?.proof_of_address && !user?.id_document ? 'Not uploaded' : ''}</span>
              </div>
              {user?.bio && (
                <div className="md:col-span-2 flex items-start gap-2 text-gray-600">
                  <User className="w-4 h-4 text-gray-400 mt-0.5" />
                  <span data-testid="profile-bio">{user.bio}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Upcoming Bookings */}
        <section className="mb-8" data-testid="upcoming-bookings-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Upcoming Bookings</h2>
            <Link to="/my-bookings" className="text-[#D65A31] text-sm font-medium hover:underline flex items-center gap-1">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {upcomingBookings.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <CalendarDays className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">No upcoming bookings yet</p>
              <Link to="/">
                <Button className="bg-[#D65A31] hover:bg-[#B84A28] text-white" data-testid="explore-btn">
                  <Search className="w-4 h-4 mr-2" /> Explore experiences
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingBookings.map((booking) => (
                <div key={booking.id} className="bg-white rounded-xl border p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`booking-${booking.id}`}>
                  <div className="flex-1">
                    <h3 className="font-semibold">{booking.experience_title}</h3>
                    <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{booking.date}</span>
                      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{booking.guests} guest{booking.guests > 1 ? 's' : ''}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{booking.time_slot || 'TBD'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                      booking.status === 'confirmed' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
                    }`} data-testid={`booking-status-${booking.id}`}>
                      {booking.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                    </span>
                    {booking.status === 'confirmed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setContactModal(booking)}
                        className="text-[#D65A31] border-[#D65A31] hover:bg-[#FDF8F4]"
                        data-testid={`contact-host-btn-${booking.id}`}
                      >
                        <Mail className="w-3.5 h-3.5 mr-1" /> Contact Host
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recently Browsed */}
        <section className="mb-8" data-testid="recently-browsed-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Recently Browsed</h2>
            <Link to="/" className="text-[#D65A31] text-sm font-medium hover:underline flex items-center gap-1">
              Browse more <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {recentlyBrowsed.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center">
              <Eye className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">No recently viewed experiences</p>
              <Link to="/">
                <Button className="bg-[#D65A31] hover:bg-[#B84A28] text-white" data-testid="start-browsing-btn">
                  <Search className="w-4 h-4 mr-2" /> Start exploring
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentlyBrowsed.map((exp) => (
                <Link key={exp.id} to={`/experience/${exp.id}`} className="bg-white rounded-xl border overflow-hidden hover:shadow-md transition-shadow group" data-testid={`browsed-${exp.id}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={exp.images?.[0] || '/placeholder.jpg'}
                      alt={exp.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-gray-500">{exp.location}</p>
                    <h3 className="font-medium text-sm line-clamp-1">{exp.title}</h3>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-sm font-semibold">{formatPrice(exp.price)}/person</span>
                      {exp.average_rating > 0 && (
                        <span className="flex items-center gap-0.5 text-xs text-gray-500">
                          <Star className="w-3 h-3 fill-current text-yellow-500" /> {exp.average_rating?.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Quick Actions */}
        <section className="mb-8">
          <div className="bg-gradient-to-r from-[#D65A31] to-[#FF6B35] rounded-2xl p-6 text-white flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold">Looking for your next meal adventure?</h2>
              <p className="opacity-90 text-sm mt-1">Explore hundreds of unique dining experiences near you</p>
            </div>
            <Link to="/">
              <Button className="bg-white text-[#D65A31] hover:bg-gray-100 font-semibold" data-testid="search-experiences-btn">
                <Search className="w-4 h-4 mr-2" /> Search Experiences
              </Button>
            </Link>
          </div>
        </section>
      </main>

      {/* Contact Host Modal */}
      <Dialog open={!!contactModal} onOpenChange={() => { setContactModal(null); setContactMessage(''); }}>
        <DialogContent className="max-w-md" data-testid="contact-host-dialog">
          <DialogTitle className="text-lg font-semibold">Contact Host</DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Send a message to the host about your booking for <strong>{contactModal?.experience_title}</strong>
          </DialogDescription>
          <div className="mt-4 space-y-4">
            <Textarea
              value={contactMessage}
              onChange={(e) => setContactMessage(e.target.value)}
              placeholder="Hi! I have a question about the experience..."
              rows={5}
              maxLength={2000}
              data-testid="contact-message-input"
            />
            <p className="text-xs text-gray-400 text-right">{contactMessage.length}/2000</p>
            <Button
              onClick={handleContactHost}
              disabled={sendingMessage || contactMessage.length < 5}
              className="w-full bg-[#D65A31] hover:bg-[#B84A28] text-white"
              data-testid="send-message-btn"
            >
              {sendingMessage ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send Message</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default GuestProfilePage;
